import type { components } from "@cntrl/api-client";

/** The wire row, every field of which the generator marks nullable. */
type WirePlanOption = components["schemas"]["PlanOption"];

/**
 * A plan option with the fields this module needs PROVEN present. The generated schema marks every property
 * optional, so a formatter reading it raw has to `??` at each use and would print a plausible `$0` for a row
 * that arrived malformed. Normalising once at the boundary instead means a row we cannot price is DROPPED —
 * a button that offers no price is worse than no button, and the fabricated-zero rule applies to a price as
 * much as to a follower count.
 */
export interface PlanOption {
  planKey: string;
  name: string;
  amountCents: number;
  currency: string;
  interval: string;
  tier: string;
}

function normalize(o: WirePlanOption | undefined): PlanOption | null {
  if (!o?.planKey || !o.interval || typeof o.amountCents !== "number" || o.amountCents <= 0) return null;
  return {
    planKey: o.planKey,
    name: o.name ?? "Plan",
    amountCents: o.amountCents,
    currency: o.currency ?? "USD",
    interval: o.interval,
    tier: o.tier ?? "standard",
  };
}

/**
 * May this seat, on this rail, act on the plan at all — the server's single verdict, read the ONE way.
 *
 * `change` means the Stripe rail AND a caller who may act (`(root ∈ {artist, management}) AND money access`);
 * every other value — a Shopify band, a comp, a lifetime, a booking agent — is answered by the Plan ROW,
 * which states the plan and says why there is nothing to do. It lives here rather than beside the block
 * because THREE pieces read it (the body, the controls, and the dialog deciding whether to draw a footer at
 * all), and a verdict re-derived at three call sites is the second implementation this DTO exists to prevent.
 */
export function canChangePlan(
  plan: { action?: string | null } | null | undefined,
): boolean {
  return plan?.action === "change";
}

/** Every priced row the server offered, in the order it sent them (standard then pro, annual then monthly). */
export function planOptions(options: readonly WirePlanOption[] | null | undefined): PlanOption[] {
  return (options ?? []).map(normalize).filter((o): o is PlanOption => o !== null);
}

/**
 * How a plan's price is WORDED — the one authority, because the same figure appears on the upgrade button,
 * the ghost second cadence and (later) the plan row, and three local formatters is `holdCountdown` again.
 *
 * Every number here comes from the `core.plans` row the band will actually be CHARGED (root invariant 3):
 * prices are data, never constants. The MVP's button said "$250/mo billed annually" as a literal while its
 * own SKU registry said $275 — a conflict that could only exist because the copy and the charge came from
 * two places. Here $3,000/yr renders as "$250/mo billed annually" because that is what dividing the annual
 * row by twelve gives, so the two cannot disagree.
 */

/** `$250`, `$1,440` — whole dollars, because every price in this catalogue is one. */
export function money(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/**
 * The button's price phrase. An ANNUAL plan is expressed per month — "$250/mo billed annually" — because
 * that is how the ladder is compared and how the shipped copy always read; a monthly plan says
 * "$300 month-to-month". A year that does not divide into whole cents falls back to the annual figure rather
 * than printing a rounded per-month number nobody is charged.
 */
export function priceLabel(option: PlanOption): string {
  if (option.interval === "year") {
    const perMonth = option.amountCents / 12;
    return Number.isInteger(perMonth)
      ? `${money(perMonth, option.currency)}/mo billed annually`
      : `${money(option.amountCents, option.currency)}/yr`;
  }
  return `${money(option.amountCents, option.currency)} month-to-month`;
}

/** The Pro cadences, annual first — annual leads, mirroring standard Artist CNTRL's annual-first pricing. */
export function proOptions(options: readonly WirePlanOption[] | null | undefined): PlanOption[] {
  return planOptions(options).filter((o) => o.tier === "pro");
}

/**
 * The Standard row a downgrade lands on — **the one on the band's CURRENT cadence**.
 *
 * Taking simply the first standard row put a Pro MONTHLY band onto Standard ANNUAL, because the catalogue is
 * ordered annual-first: a downgrade would quietly change how often they are billed, which is a second
 * decision nobody asked for. `ChangePlanAsync` refuses a same-tier cadence change precisely because that
 * choice is not the product's to make; making it as a side effect of a downgrade is the same mistake through
 * a side door. Found by walking the ladder end to end against the fake, not by reading it.
 *
 * Falls back to any standard row when the current cadence has no counterpart — better a downgrade on the
 * wrong cadence than a Downgrade button that does nothing.
 */
export function standardOption(
  options: readonly WirePlanOption[] | null | undefined,
  currentInterval?: string | null,
): PlanOption | undefined {
  const standard = planOptions(options).filter((o) => o.tier === "standard");
  return standard.find((o) => o.interval === currentInterval) ?? standard[0];
}

/** "12 October 2026" in the reader's locale — the date a scheduled downgrade takes effect. */
export function planDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Where the band is billed from, in words — the right-hand fact on the Plan row.
 *
 * A LABEL, so it lives on the client; the VERDICT it labels (`billedVia`) is the server's, resolved from
 * three columns in one place so this map never has to stitch them together.
 *
 * Deliberately ONE WORD each: it sits beside the plan name on a row that also carries an action, and the row
 * reads "Artist CNTRL Pro · Annual · Comped". A `lifetime` band's plan and source are the same word, which
 * {@link billingLine} drops rather than printing twice.
 */
export function billedViaLabel(billedVia: string | null | undefined): string {
  switch (billedVia) {
    case "stripe": return "Stripe";
    case "shopify": return "Shopify";
    // The one value no other band ever sees (PO, 2026-09-10) — it exists so a comped band's row tells the
    // truth instead of being hidden, which is what the old blanket rule did.
    case "comp": return "Comped";
    case "lifetime": return "Lifetime";
    // ART-348: the 14-day free trial a newly approved band starts on. Nothing bills yet.
    case "trial": return "Free trial";
    default: return "—";
  }
}

/** A cadence in words, or null for anything that is not a recurring one (a lifetime row's `lifetime`). */
export function intervalLabel(interval: string | null | undefined): string | null {
  return interval === "year" ? "Annual" : interval === "month" ? "Monthly" : null;
}

/** The band's current plan in words: "Artist CNTRL Pro · Annual". Null plan key ⇒ they have not bought one. */
export function planLabel(
  planKey: string | null | undefined,
  options: readonly { planKey?: string | null; name?: string | null; interval?: string | null }[] | null | undefined,
): string {
  if (!planKey) return "No plan";
  const row = (options ?? []).find((o) => o.planKey === planKey);
  // The NAME comes from the catalogue row being charged — never from the tier (which names a product, not a
  // plan) and never from the key. A plan the catalogue no longer offers still has a key worth printing.
  if (!row?.name) return planKey === "lifetime" ? "Lifetime" : planKey;
  const cadence = intervalLabel(row.interval);
  return cadence ? `${row.name} · ${cadence}` : row.name;
}

/**
 * The PLAN row's value: WHAT the band is on, and nothing else (PO, 2026-09-11).
 *
 * The cadence and the billing source moved to the BILLING row, where they answer that row's question. One
 * row reading "Artist CNTRL Pro · Annual · Stripe" beside a BILLING row reading only a date was three facts
 * about the money stacked on the row about the product.
 */
export function planName(
  planKey: string | null | undefined,
  options: readonly { planKey?: string | null; name?: string | null; interval?: string | null }[] | null | undefined,
): string {
  return planLabel(planKey, stripIntervals(options));
}

/**
 * The BILLING row's value: the cadence, where the money comes from, and when it next moves.
 *
 * **A CADENCE IS A BILLING FACT, so a plan that does not bill has none** (PO, 2026-09-10, carried over from
 * the single-row shape). A comped band is not on "Annual": the annual/monthly split exists to say when the
 * next charge lands, and nothing will ever charge. Same for a lifetime grant.
 *
 * The source is dropped when it would REPEAT the plan's own name — a lifetime grant is both what you are on
 * and the reason nothing bills, and one word on two rows reads as a rendering bug. That rule used to live
 * inside one string; splitting the rows did not retire it, it just moved what it compares.
 *
 * @param timing "Renews …" / "Ends …" / "Changes …", or null. The caller owns it because only the caller
 * knows the pending state; three outcomes, and collapsing any two of them tells a band leaving that it renews.
 */
export function billingLine(
  planKey: string | null | undefined,
  billedVia: string | null | undefined,
  options: readonly { planKey?: string | null; name?: string | null; interval?: string | null }[] | null | undefined,
  timing?: string | null,
): string {
  const bills = billedVia !== "comp" && billedVia !== "lifetime" && billedVia !== "trial";
  const row = (options ?? []).find((o) => o.planKey === planKey);
  const cadence = bills ? intervalLabel(row?.interval) : null;
  const via = billedViaLabel(billedVia);
  const parts = [
    cadence,
    via === "—" || via === planName(planKey, options) ? null : via,
    timing || null,
  ].filter((x): x is string => Boolean(x));
  return parts.length > 0 ? parts.join(" · ") : "—";
}

/** The catalogue with its cadences removed — what a plan is named by when the cadence is not its row's
 * business. Dropping the interval here rather than branching inside {@link planLabel} keeps that function a
 * straight catalogue lookup. */
function stripIntervals(
  options: readonly { planKey?: string | null; name?: string | null; interval?: string | null }[] | null | undefined,
) {
  return (options ?? []).map((o) => ({ ...o, interval: null }));
}
